import { ChatMessage } from "../Chats/ChatMessage";
import { IChatMessage, IChatMessageFragment, IFileUpload } from "Utils/Interfaces";
import { __ajax } from "../AJAX/ajax";
import { __pusher } from "../Pusher/pusher";
import { __config } from "../Configs/Config";
import { ChatContext } from "Utils/Contexts";
import { ChatMessageFragment } from "Utils/Chats/ChatMessageFragment";
import { __chats } from "Utils/Chats/Chats";
import { __utils } from "Utils/Utils";
import { MsgReceive } from 'Utils/Explorer/MsgReceive';
import { __chatState } from "Utils/Chats/ChatState";

class SBRoot {
    /**
     * Sends user details to assign contact.
     * @param chatId The ID of the chat.
     * @param name The name of the user.
     * @param email The email of the user.
     * @returns An empty string.
     */
    public sendUserDetails(chatId: string, name: string, email: string): string {

        const queryParams = new URLSearchParams(window.location.search);
        const uid = queryParams.get('chatCode'); // Assuming chatCode is the unique identifier for the chat
        const domain = queryParams.get('url'); // This is the parent URL where the widget is installed

        const jsonData: any = {
            uid: this.getUid(),
            name: name,
            email: email,
            chatId: chatId,
            domain: domain
        };

        __ajax.sendAjaxRequest('post', '/chat/caseAssignContact', JSON.stringify(jsonData), (res: any) => {

            const contactId = res.contact;
            const uid = this.getUid();

            localStorage.setItem(`contact_id${uid}`, contactId);
            localStorage.setItem(`contact_name${uid}`, name);
            localStorage.setItem(`contact_email${uid}`, email);

        });

        return "";

    }

    /**
     * Retrieves contact ID and optionally creates a case.
     * @param name The name of the user.
     * @param email The email of the user.
     * @param createCase Whether to create a case.
     * @param caseId The case ID.
     * @returns An empty string.
     */
    public getContactId(name: string, email: string, createCase: boolean, caseId: string): string {

        // Decode the email address
        const decodedEmail = decodeURIComponent(email);

        const jsonData: any = {
            uid: this.getUid(),
            name: name,
            email: decodedEmail,
        };

        __ajax.sendAjaxRequest('post', '/chat/getContact', JSON.stringify(jsonData), (res: any) => {

            const contactId = res.contact;
            const uid = this.getUid();

            localStorage.setItem(`contact_id${uid}`, contactId);
            localStorage.setItem(`contact_name${uid}`, name);
            localStorage.setItem(`contact_email${uid}`, decodedEmail);

            if (caseId) {
                this.sendUserDetails(caseId, name, decodedEmail);
            }

        });

        return "";
    }

    /**
     * Sends the user's location information to the server.
     * @param chatId The ID of the chat.
     */
    public sendLocation(chatId: string) {

        let contact_id;

        //get the contact id 
        if (localStorage.getItem(`contact_id${this.getUid()}`) && localStorage.getItem(`contact_id${this.getUid()}`) !== '' && localStorage.getItem(`contact_id${this.getUid()}`) !== null && localStorage.getItem(`contact_id${this.getUid()}`) !== 'null') {
            contact_id = localStorage.getItem(`contact_id${this.getUid()}`);
        }

        if (contact_id === null || contact_id === 'null' && __utils.isValidEmail(decodeURIComponent(window.location.search.split("&")[1].replace('email=', '')))) {
            var ct = this.getContactId('', decodeURIComponent(window.location.search.split("&")[1].replace('email=', '')), false, '');

            __utils.Sleep(1000);

            contact_id = localStorage.getItem(`contact_id${this.getUid()}`);
        }

        const userUid = this.getUserUid();
        const jsonData = {
            uid: this.getUid(),
            chat_id: chatId,
            contact_id: contact_id,
            page_url: decodeURIComponent(window.location.search.split("&")[2].split("=")[1]),
            user_uid: userUid,
            ...this.getBrowserInfo(),
        };

        const jsonDataString = JSON.stringify(jsonData);
        const sessionKey = `locationSent_${btoa(jsonDataString)}`;
        const pusherChannelKey = `pusherChannelOpened_${chatId}`;

        if (!sessionStorage.getItem(sessionKey)) {
            // Send data to the server if not already sent in this session
            __ajax.sendAjaxRequest('post', '/chat/setLocation', jsonDataString, (res: any) => {
                //console.log('Location data sent to server:', res);
            });

            // Mark this data as sent in session storage
            sessionStorage.setItem(sessionKey, 'true');
        }

        if (!sessionStorage.getItem(pusherChannelKey)) {
            // Open pusher channel if not already opened in this session
            __pusher.createChannel(chatId);

            // Mark this pusher channel as opened in session storage
            sessionStorage.setItem(pusherChannelKey, 'true');
        }
    }

    /**
     * Starts a chat with the given chat message.
     * @param chatMessage The chat message.
     * @returns An empty string.
     */
    public startChat(chatMessage: ChatMessage): string {
        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatMessage.chatId,
            contactId: localStorage.getItem(`contact_id${this.getUid()}`) || '',
        };

        // Placeholder for chat existence check and creation logic
        // __ajax.sendAjaxRequest('post', '/chat/exists', JSON.stringify(jsonData), function(res: any) {
        //     ...
        // });

        return "";
    }

    /**
     * Starts a chat with the given chat ID.
     * @param chatId The chat ID.
     * @returns An empty string.
     */
    public startChatWithId(chatId: string): string {
        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatId,
            contactId: localStorage.getItem(`contact_id${this.getUid()}`) || '',
        };

        __ajax.sendAjaxRequest('post', '/chat/exists', JSON.stringify(jsonData), (res: any) => {
            const chatExists = res.exists;
            const chatState = __chatState.get(chatId);

            if (chatState?.respondedUserOptions?.includes("unknown")) {
                if (chatExists) {
                    this.sendLocation(chatId);
                } else {
                    __ajax.sendAjaxRequest('post', '/chat/create', JSON.stringify(jsonData), (res2: any) => {
                        this.sendLocation(chatId);
                    });
                }
            }
        });

        return "";
    }

    public sendMessage(chatMessageId: string, chatMessage: ChatMessage, messageText: string, isTalkingToBot: boolean): string {

        if (!messageText) return "";

        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatMessageId,
            message: messageText,
            isTalkingToBot: isTalkingToBot,
            contactId: localStorage.getItem(`contact_id${this.getUid()}`) || '',
            userId: '',
        };

        //console.log('sendMessage chatMessage: ' + JSON.stringify(chatMessage));

        __ajax.sendAjaxRequest('post', '/chat/addMessage', JSON.stringify(jsonData), (res: any) => {

            const messageId = res.messageId;
            const chatId = res.chatId;
            chatMessage.id = messageId;
            chatMessage.chatId = chatId;

            sessionStorage.setItem(`last_Chat${this.getUid()}`, chatMessage.chatId.toString());

            if (!isTalkingToBot) {
                __pusher.createChannel(chatMessageId);
            }

            this.sendLocation(chatMessage.chatId.toString());

        });

        return "";
    }

    public sendMessageBot(chatMessageId: string, chatMessage: ChatMessage, messageText: string): string {

        if (!messageText) return "";

        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatMessageId,
            message: messageText,
            contactId: '',
            userId: '-1',
        };

        __ajax.sendAjaxRequest('post', '/chat/addBotMessage', JSON.stringify(jsonData), (res: any) => {
            const messageId = res.messageId;
            const chatId = res.chatId;
            chatMessage.id = messageId;
            chatMessage.chatId = chatId;

            sessionStorage.setItem(`last_Chat${this.getUid()}`, chatMessage.chatId.toString());
            this.sendLocation(chatMessage.chatId.toString());

            //console.log('sendMessageBot ADDED');

        });

        return "";
    }

    /**
     * Sends a message fragment in the chat.
     * @param chatMessageId The chat message ID.
     * @param chatMessage The chat message fragment.
     * @param messageText The message text.
     * @returns An empty string.
     */
    public sendMessageFragment(chatMessageId: string, chatMessage: ChatMessageFragment | undefined, messageText: string): string {
        if (!messageText) return "";

        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatMessageId,
            message: messageText,
            contactId: localStorage.getItem(`contact_id${this.getUid()}`) || '',
            userId: '',
        };

        __ajax.sendAjaxRequest('post', '/chat/addMessage', JSON.stringify(jsonData), (res: any) => {
            const messageId = res.messageId;
            if (chatMessage) {
                chatMessage.id = messageId;
            }
        });

        this.sendLocation(chatMessageId);

        return "";
    }

    /**
     * Retrieves the bot configuration from the server.
     */
    public getBotConfig() {
        const jsonData: any = { uid: this.getUid() };

        __ajax.sendAjaxRequest('post', '/chat/bot', JSON.stringify(jsonData), (res: any) => {
            localStorage.setItem('bot', res);
        });
    }

    /**
     * Sets the messages in the chat as read.
     * @param chatId The chat ID.
     */
    public setMessageRead(chatId: string) {
        const jsonData: any = {
            uid: this.getUid(),
            chatId: chatId,
            contactId: localStorage.getItem(`contact_id${this.getUid()}`) || '',
        };

        __ajax.sendAjaxRequest('post', '/chat/messageread', JSON.stringify(jsonData), (res: any) => {
            // Handle the response if needed
        });
    }

    /**
     * Sends a file in the chat.
     * @param file The file to send.
     * @param chatId The chat ID.
     * @param _callback The callback function.
     */
    public sendFile(file: File, chatId: string, _callback: (fileUrl: string) => void) {
        const formData = new FormData();
        const uid = this.getUid();

        formData.append("upload", file);
        formData.append("chatKey", uid);
        formData.append("chatID", chatId);
        formData.append("contactId", localStorage.getItem(`contact_id${uid}`) || '');

        __ajax.sendAjaxRequestFile('post', '/chat/uploadAttach', formData, (res: any) => {
            const fileUrl = JSON.parse(res).fileUrl;
            _callback(fileUrl);
        });
    }

    /**
     * Loads missed chats (method placeholder).
     */
    public loadMissedChats() {
        // Load missed chats logic
        const missedChats = JSON.parse(localStorage.getItem("chats") || "{}");
        const chatIds = Object.keys(missedChats);
        if (chatIds.length > 0) {
            document.getElementById("continue-conversations")?.classList.remove("hide-hard");
        }
    }

    /**
     * Sends a survey message in the chat (method placeholder).
     * @param chatMessage The chat message.
     * @returns An empty string.
     */
    public sendSurvey(chatMessage: IChatMessage): string {
        return "";
    }

    /**
     * Closes the chat (method placeholder).
     * @param chatMessage The chat message.
     * @returns An empty string.
     */
    public closeChat(chatMessage: IChatMessage): string {
        return "";
    }

    /**
     * Retrieves the top articles (method placeholder).
     * @returns An empty string.
     */
    public getTopArticles(): string {
        const jsonData: any = { uid: this.getUid() };

        __ajax.sendAjaxRequest('post', '/chat/suggestedArticles', JSON.stringify(jsonData), (res: any) => {
            // Handle the response if needed
        });

        return "";
    }

    /**
     * Retrieves the unique user ID from the URL.
     * @returns The unique user ID.
     */
    private getUid(): string {
        return window.location.search.substr(1).split("&")[0].split("=")[1];
    }

    /**
     * Retrieves the user's UUID.
     * @returns The user's UUID.
     */
    private getUserUid(): string {
        const uid = this.getUid();
        let userUid = localStorage.getItem(`userUid${uid}`);

        if (!userUid) {
            userUid = __utils.getUuidv4();
            localStorage.setItem(`userUid${uid}`, userUid);
        }

        return userUid;
    }

    /**
     * Retrieves browser information.
     * @returns An object containing browser information.
     */
    private getBrowserInfo(): { browser: string; browser_version: string; operating_system: string } {
        const nAgt = navigator.userAgent;
        let browserName = navigator.appName;
        let fullVersion = '' + parseFloat(navigator.appVersion);
        let majorVersion = parseInt(navigator.appVersion, 10);

        if (nAgt.indexOf("OPR") != -1) {
            browserName = "Opera";
            fullVersion = nAgt.substring(nAgt.indexOf("OPR") + 4);
        } else if (nAgt.indexOf("Edg") != -1) {
            browserName = "Microsoft Edge";
            fullVersion = nAgt.substring(nAgt.indexOf("Edg") + 4);
        } else if (nAgt.indexOf("MSIE") != -1) {
            browserName = "Microsoft Internet Explorer";
            fullVersion = nAgt.substring(nAgt.indexOf("MSIE") + 5);
        } else if (nAgt.indexOf("Chrome") != -1) {
            browserName = "Chrome";
            fullVersion = nAgt.substring(nAgt.indexOf("Chrome") + 7);
        } else if (nAgt.indexOf("Safari") != -1) {
            browserName = "Safari";
            fullVersion = nAgt.substring(nAgt.indexOf("Safari") + 7);
            if (nAgt.indexOf("Version") != -1) {
                fullVersion = nAgt.substring(nAgt.indexOf("Version") + 8);
            }
        } else if (nAgt.indexOf("Firefox") != -1) {
            browserName = "Firefox";
            fullVersion = nAgt.substring(nAgt.indexOf("Firefox") + 8);
        } else if ((nAgt.lastIndexOf(' ') + 1) < nAgt.lastIndexOf('/')) {
            browserName = nAgt.substring(nAgt.lastIndexOf(' ') + 1, nAgt.lastIndexOf('/'));
            fullVersion = nAgt.substring(nAgt.lastIndexOf('/') + 1);
            if (browserName.toLowerCase() == browserName.toUpperCase()) {
                browserName = navigator.appName;
            }
        }

        if ((majorVersion = parseInt('' + fullVersion, 10)) && isNaN(majorVersion)) {
            fullVersion = '' + parseFloat(navigator.appVersion);
            majorVersion = parseInt(navigator.appVersion, 10);
        }

        let OSName = "Unknown OS";
        if (navigator.appVersion.indexOf("Win") != -1) OSName = "Windows";
        if (navigator.appVersion.indexOf("Mac") != -1) OSName = "MacOS";
        if (navigator.appVersion.indexOf("X11") != -1) OSName = "UNIX";
        if (navigator.appVersion.indexOf("Linux") != -1) OSName = "Linux";

        return {
            browser: browserName,
            browser_version: fullVersion,
            operating_system: OSName
        };
    }
}

export const __sb = new SBRoot();
